import { Component, OnInit, Inject, PLATFORM_ID } from '@angular/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { Router, ActivatedRoute } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { take } from 'rxjs';
import { AuthService } from '../../core/services/auth.service';

@Component({
  selector: 'app-callback',
  standalone: true,
  templateUrl: './callback.component.html',
  styleUrl: './callback.component.scss'
})
export class CallbackComponent implements OnInit {
  constructor(
    private oidc: OidcSecurityService,
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  ngOnInit() {
    const qp = this.route.snapshot.queryParamMap;
    const code  = qp.get('code');
    const state = qp.get('state');

    if (isPlatformBrowser(this.platformId) && code && state) {
      const doneKey = `oidc_done_${state}`;
      if (sessionStorage.getItem(doneKey)) {
        this.oidc.isAuthenticated$.pipe(take(1)).subscribe(({ isAuthenticated }) => {
          if (isAuthenticated) {
            // No redirigir manualmente, dejar que AuthService maneje la redirección automática
            // según el perfil del usuario
            console.log('[Callback] Usuario autenticado, redirección será manejada por AuthService');
          } else {
            this.router.navigate(['/login']);
          }
        });
        return;
      }
      sessionStorage.setItem(doneKey, '1');

      this.oidc.checkAuth().pipe(take(1)).subscribe({
        next: ({ isAuthenticated }) => {
          if (isAuthenticated) {
            console.log('[Callback] Autenticación exitosa, obteniendo userId...');
            // Asegurar userId antes de navegar
            this.authService.ensureUserId()
              .then(() => {
                console.log('[Callback] userId listo, redirigiendo por perfil');
                this.navigateBasedOnUserProfile();
                //setTimeout(()=> this.navigateBasedOnUserProfile(), 1000) ;
              })
              .catch(err => {
                console.error('[Callback] Error obteniendo userId. Bloqueando navegación dependiente.', err);
                // Mostrar mensaje simple y permanecer en la página o enviar al login
                alert('No se pudo obtener su acceso de usuario. Intente nuevamente.');
                this.router.navigate(['/login']);
              });
          } else {
            this.router.navigate(['/login']);
          }
        },
        error: (err) => {
          const msg = String(err || '');
          if (msg.includes('could not find matching config for state')) {
            // En caso de error de configuración, no redirigir manualmente
            console.log('[Callback] Error de configuración, redirección será manejada por AuthService');
            return;
          }
          console.error('checkAuth error:', err);
          this.router.navigate(['/login']);
        },
      });
      return;
    }

    if (isPlatformBrowser(this.platformId)) {
      this.oidc.isAuthenticated$.pipe(take(1)).subscribe(({ isAuthenticated }) => {
        if (isAuthenticated) {
          console.log('[Callback] Usuario ya autenticado, asegurando userId...');
          this.authService.ensureUserId()
            .then(() => this.navigateBasedOnUserProfile())
            .catch(err => {
              console.error('[Callback] Error obteniendo userId. Bloqueando navegación dependiente.', err);
              alert('No se pudo obtener su acceso de usuario. Intente nuevamente.');
              this.router.navigate(['/login']);
            });
        } else {
          this.router.navigate(['/login']);
        }
      });
    }
  }

  private navigateBasedOnUserProfile(): void {
    console.log('[Callback] Determinando ruta basada en perfil de usuario...');
    
    // Obtener el usuario actual del AuthService
    const currentUser = this.authService.getCurrentUser();
    
    if (!currentUser) {
      console.log('[Callback] No hay usuario actual, navegando a dashboard por defecto');
      this.router.navigateByUrl('/dashboards/home');
      return;
    }

    const groups: string[] = currentUser['cognito:groups'] || currentUser['roles'] || [];
    console.log('[Callback] Grupos del usuario:', groups);
    
    let targetRoute = '/dashboards/home'; // Default route
    
    if (groups.includes('novedades')) {
      targetRoute = '/mis-novedades';
      console.log('[Callback] Usuario tiene grupo "novedades", navegando a /mis-novedades');
    } else if (groups.includes('Novedades_Operador_DCS')) {
      targetRoute = '/dcs';
      console.log('[Callback] Usuario tiene grupo "Novedades_Operador_DCS", navegando a /dcs');
    } else if (groups.includes('Novedades_Operador_Ferrico')) {
      targetRoute = '/cloruro-ferrico';
      console.log('[Callback] Usuario tiene grupo "Novedades_Operador_Ferrico", navegando a /cloruro-ferrico');
    } else if (groups.includes('Novedades_Salmuera_II')) {
      targetRoute = '/salmuera-ii';
      console.log('[Callback] Usuario tiene grupo "Novedades_Salmuera_II", navegando a /salmuera-ii');
    } else if (groups.includes('Novedades_Salmuera_I')) {
      targetRoute = '/salmuera-i';
      console.log('[Callback] Usuario tiene grupo "Novedades_Salmuera_I", navegando a /salmuera-i');
    } else if (groups.includes('Novedades_Compresion')) {
      targetRoute = '/comprension';
      console.log('[Callback] Usuario tiene grupo "Novedades_Compresion", navegando a /comprension');
    } else if (groups.includes('Novedades_Electrolizadores')) {
      targetRoute = '/electrolizadores';
      console.log('[Callback] Usuario tiene grupo "Novedades_Electrolizadores", navegando a /electrolizadores');
    } else if (groups.includes('Novedades_Subestacion')) {
      targetRoute = '/subestacion';
      console.log('[Callback] Usuario tiene grupo "Novedades_Subestacion", navegando a /subestacion');
    } else if (groups.includes('Novedades_Movimiento_Producto')) {
      targetRoute = '/mov-prod';
      console.log('[Callback] Usuario tiene grupo "Novedades_Movimiento_Producto", navegando a /mov-prod');
    } else if (groups.includes('cde')) {
      targetRoute = '/dashboards/home';
      console.log('[Callback] Usuario tiene grupo "cde", navegando a /dashboards/home');
    } else {
      console.log('[Callback] Usuario no tiene grupos reconocidos, navegando por defecto a /dashboards/home');
    }
    
    console.log('[Callback] Navegando a:', targetRoute);
    console.log('[Callback] URL actual antes de navegar:', this.router.url);
    
    this.router.navigateByUrl(targetRoute).then(success => {
      console.log('[Callback] Navegación exitosa:', success);
      console.log('[Callback] URL actual después de navegar:', this.router.url);
      
      if (!success) {
        console.error('[Callback] Navegación falló, intentando navegación por defecto');
        this.router.navigateByUrl('/dashboards/home').then(defaultSuccess => {
          console.log('[Callback] Navegación por defecto exitosa:', defaultSuccess);
        });
      }
    }).catch(error => {
      console.error('[Callback] Error en navegación:', error);
      // Fallback a dashboard home
      this.router.navigateByUrl('/dashboards/home');
    });
  }
}
